/*
 * 0NYX core.js — comportamento funcional comum aos três temas experimentais.
 * Sem dependências. Tudo funciona sem JS (formulários nativos da Shopify);
 * este arquivo só melhora a experiência: carrinho em gaveta, variantes,
 * filtros sem recarregar, busca preditiva e revelação de conteúdo.
 */
(() => {
  'use strict';

  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || root.dataset.motion === 'off';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const routes = window.OnyxRoutes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_url: '/cart', predictive_search_url: '/search/suggest' };
  const strings = window.OnyxStrings || {};

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------------------------------------------------------------
   * Painéis (gaveta do carrinho, menu mobile, busca)
   * Qualquer elemento [data-panel="id"] é um painel modal.
   * Botões [data-open="id"] / [data-close] abrem e fecham.
   * ------------------------------------------------------------- */
  let lastFocus = null;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function openPanel(id, opener) {
    const panel = document.querySelector(`[data-panel="${id}"]`);
    if (!panel) return false;
    closeAllPanels(false);
    lastFocus = opener || document.activeElement;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => panel.classList.add('is-open'));
    document.body.classList.add('has-panel');
    $$(`[data-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const target = $('[data-autofocus]', panel) || $(FOCUSABLE, panel);
    if (target) setTimeout(() => target.focus({ preventScroll: true }), 30);
    return true;
  }

  function closePanel(panel, restore = true) {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    const id = panel.dataset.panel;
    $$(`[data-open="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    const done = () => {
      panel.hidden = true;
    };
    if (reduceMotion()) done();
    else setTimeout(done, 320);
    if (!$('[data-panel].is-open')) document.body.classList.remove('has-panel');
    if (restore && lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  function closeAllPanels(restore = true) {
    $$('[data-panel].is-open').forEach((p) => closePanel(p, restore));
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      if (openPanel(opener.dataset.open, opener)) e.preventDefault();
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) {
      e.preventDefault();
      closePanel(closer.closest('[data-panel]'));
    }
  });

  document.addEventListener('keydown', (e) => {
    const open = $('[data-panel].is-open');
    if (!open) {
      // Atalho de busca: "/" ou Ctrl/Cmd+K abre o painel de busca, quando existir.
      const typing = /input|textarea|select/i.test(document.activeElement?.tagName || '');
      if (!typing && (e.key === '/' || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k'))) {
        if ($('[data-panel="search"]')) {
          e.preventDefault();
          openPanel('search');
        }
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      closePanel(open);
    }
    if (e.key === 'Tab') {
      const items = $$(FOCUSABLE, open).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ---------------------------------------------------------------
   * Carrinho (API AJAX oficial: /cart/add.js e /cart/change.js)
   * Section Rendering API devolve o HTML já renderizado da gaveta.
   * ------------------------------------------------------------- */
  const cartSections = () => {
    const ids = ['cart-drawer'];
    $$('[data-cart-section]').forEach((el) => ids.push(el.dataset.cartSection));
    return Array.from(new Set(ids));
  };

  function renderCartSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      const fresh = doc.querySelector('[data-cart-root]');
      const current = id === 'cart-drawer' ? $('[data-panel="cart"] [data-cart-root]') : $(`[data-cart-section="${id}"] [data-cart-root]`);
      if (fresh && current) current.replaceWith(fresh);
      const count = doc.querySelector('[data-cart-count-value]');
      if (count) updateCount(count.dataset.cartCountValue);
    });
    document.dispatchEvent(new CustomEvent('onyx:cart:updated'));
  }

  function updateCount(n) {
    $$('[data-cart-count-label]').forEach((el) => (el.textContent = `(${n})`));
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = n;
      el.hidden = Number(n) === 0;
      el.classList.remove('is-bump');
      void el.offsetWidth;
      if (!reduceMotion()) el.classList.add('is-bump');
    });
  }

  function setBusy(btn, busy) {
    if (!btn) return;
    btn.toggleAttribute('aria-busy', busy);
    btn.classList.toggle('is-loading', busy);
    btn.disabled = busy || btn.dataset.unavailable === 'true';
  }

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    if (document.body.dataset.cartType === 'page') return; // envio nativo
    e.preventDefault();
    const btn = $('[type="submit"]', form);
    const error = $('[data-form-error]', form.closest('[data-product]') || form);
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    setBusy(btn, true);
    const body = new FormData(form);
    body.append('sections', cartSections().join(','));
    body.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${routes.cart_add_url}.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || strings.addError || 'Erro');
      }
      renderCartSections(data.sections);
      if (btn) {
        btn.classList.add('is-added');
        setTimeout(() => btn.classList.remove('is-added'), 1600);
      }
      if (!openPanel('cart', btn)) window.location.href = routes.cart_url;
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
    } finally {
      setBusy(btn, false);
    }
  });

  async function changeLine(line, quantity, scope) {
    const rootEl = scope.closest('[data-cart-root]');
    rootEl?.classList.add('is-updating');
    rootEl?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSections(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError || 'Erro');
      renderCartSections(data.sections);
    } catch (err) {
      const msg = $('[data-cart-error]', rootEl || document);
      if (msg) {
        msg.textContent = err.message;
        msg.hidden = false;
      }
      rootEl?.classList.remove('is-updating');
      rootEl?.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove && document.body.dataset.cartAjax !== 'false') {
      e.preventDefault();
      changeLine(Number(remove.dataset.cartRemove), 0, remove);
    }
  });

  document.addEventListener(
    'change',
    debounce((e) => {
      const input = e.target.closest('[data-cart-qty]');
      if (!input) return;
      const q = Math.max(0, parseInt(input.value, 10) || 0);
      changeLine(Number(input.dataset.cartQty), q, input);
    }, 350)
  );

  /* ---------------------------------------------------------------
   * Quantidade (+ / −)
   * ------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    e.preventDefault();
    const input = $('input', step.closest('[data-qty]'));
    if (!input) return;
    const min = Number(input.min || 0);
    const next = Math.max(min, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyStep));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------------------------------------------------------
   * Variantes
   * ------------------------------------------------------------- */
  function initProduct(el) {
    const json = $('[data-variant-json]', el);
    if (!json) return;
    const variants = JSON.parse(json.textContent);
    const form = $('form[data-product-form]', el);
    const idInput = form && $('input[name="id"]', form);
    const fieldsets = $$('[data-option-index]', el);

    const selected = () =>
      fieldsets.map((fs) => {
        const checked = $('input:checked', fs);
        const select = $('select', fs);
        return checked ? checked.value : select ? select.value : null;
      });

    const markAvailability = (current) => {
      fieldsets.forEach((fs, i) => {
        $$('input', fs).forEach((input) => {
          const probe = current.slice();
          probe[i] = input.value;
          const match = variants.find((v) => v.options.every((o, j) => o === probe[j]));
          const ok = !!(match && match.available);
          input.closest('label, .swatch')?.classList.toggle('is-unavailable', !ok);
          input.dataset.available = ok;
        });
      });
    };

    const update = () => {
      const current = selected();
      const variant = variants.find((v) => v.options.every((o, i) => o === current[i]));
      markAvailability(current);
      $$('[data-option-value]', el).forEach((node) => {
        const idx = Number(node.dataset.optionValue);
        node.textContent = current[idx] || '';
      });
      const btn = form && $('[type="submit"]', form);
      const label = btn && $('[data-label]', btn);
      if (!variant) {
        if (btn) {
          btn.disabled = true;
          btn.dataset.unavailable = 'true';
          if (label) label.textContent = strings.unavailable || 'Indisponível';
        }
        return;
      }
      if (idInput) idInput.value = variant.id;
      $$('[data-price-now]', el).forEach((n) => (n.textContent = variant.price));
      $$('[data-price-was]', el).forEach((n) => {
        n.textContent = variant.compare || '';
        n.hidden = !variant.compare;
      });
      $$('[data-price]', el).forEach((n) => n.classList.toggle('price--sale', !!variant.compare));
      $$('[data-stock]', el).forEach((n) => {
        n.dataset.state = variant.available ? 'in' : 'out';
        n.textContent = variant.available ? strings.available || 'Disponível' : strings.soldOut || 'Esgotado';
      });
      if (btn) {
        btn.dataset.unavailable = String(!variant.available);
        btn.disabled = !variant.available;
        if (label) label.textContent = variant.available ? strings.addToCart || 'Adicionar ao carrinho' : strings.soldOut || 'Esgotado';
      }
      if (variant.media) {
        el.dispatchEvent(new CustomEvent('onyx:media', { detail: { id: variant.media }, bubbles: true }));
      }
      if (el.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    el.addEventListener('change', (e) => {
      if (e.target.closest('[data-option-index]')) update();
    });
    markAvailability(selected());
  }

  /* Galeria: mostra a mídia da variante e liga miniaturas à imagem principal.
   * Modo "slider": uma foto por vez, com setas, miniaturas, swipe (scroll-snap) e teclado. */
  function initGallery(el) {
    const slides = $$('[data-media-id]', el);
    const thumbs = $$('[data-thumb]', el);
    if (!slides.length) return;
    const track = $('[data-gallery-track]', el);
    const counter = $('[data-gallery-counter]', el);
    const prev = $('[data-gallery-prev]', el);
    const next = $('[data-gallery-next]', el);
    const thumbList = $('[data-gallery-thumbs]', el);
    const slider = el.dataset.galleryMode === 'slider';
    const alias = el.dataset.galleryAlias;
    let index = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));

    const mark = (i) => {
      index = i;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      const id = slides[i].dataset.mediaId;
      thumbs.forEach((t) => {
        const on = t.dataset.thumb === id;
        t.setAttribute('aria-current', String(on));
        if (on && thumbList) {
          const li = t.parentElement;
          const behavior = reduceMotion() ? 'auto' : 'smooth';
          if (thumbList.scrollHeight > thumbList.clientHeight + 4) {
            // Miniaturas em coluna (desktop)
            const top = li.offsetTop - thumbList.offsetTop;
            if (top < thumbList.scrollTop || top + li.offsetHeight > thumbList.scrollTop + thumbList.clientHeight) {
              thumbList.scrollTo({ top: top - (thumbList.clientHeight - li.offsetHeight) / 2, behavior });
            }
          } else {
            const left = li.offsetLeft - thumbList.offsetLeft;
            if (left < thumbList.scrollLeft || left + li.offsetWidth > thumbList.scrollLeft + thumbList.clientWidth) {
              thumbList.scrollTo({ left: left - (thumbList.clientWidth - li.offsetWidth) / 2, behavior });
            }
          }
        }
      });
      if (counter) counter.textContent = `${i + 1} / ${slides.length}`;
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };
    const go = (i, smooth = true) => {
      i = Math.max(0, Math.min(slides.length - 1, i));
      const slide = slides[i];
      mark(i);
      if (track && track.scrollWidth > track.clientWidth + 4) {
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      } else if (smooth && el.dataset.galleryMode === 'stack') {
        slide.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    };
    const activate = (id, smooth = true) => {
      let i = slides.findIndex((s) => s.dataset.mediaId === String(id));
      // A foto principal do catálogo tem uma foto nova equivalente, que abre a galeria
      if (alias && String(id) === alias) i = 0;
      if (i > -1) go(i, smooth);
    };

    thumbs.forEach((t) => t.addEventListener('click', () => activate(t.dataset.thumb)));
    prev?.addEventListener('click', () => go(index - 1));
    next?.addEventListener('click', () => go(index + 1));
    el.closest('[data-product]')?.addEventListener('onyx:media', (e) => activate(e.detail.id));

    if (slider && track) {
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
        else if (e.key === 'Home') { e.preventDefault(); go(0); }
        else if (e.key === 'End') { e.preventDefault(); go(slides.length - 1); }
      });
      // Swipe e rolagem: a foto mais visível vira a ativa
      let raf = null;
      track.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i !== index && slides[i]) mark(i);
        });
      }, { passive: true });
      const start = el.dataset.galleryInitial;
      if (start) activate(start, false); else mark(index);
      window.addEventListener('resize', () => go(index, false));
    } else if (counter && track && 'IntersectionObserver' in window) {
      // Contador do slide visível no mobile (modo empilhado)
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting && en.intersectionRatio > 0.6) {
              counter.textContent = `${slides.indexOf(en.target) + 1} / ${slides.length}`;
            }
          });
        },
        { root: track, threshold: [0.6] }
      );
      slides.forEach((s) => io.observe(s));
    }
  }

  /* ---------------------------------------------------------------
   * Filtros e ordenação (Storefront Filtering nativo + Section Rendering)
   * ------------------------------------------------------------- */
  function initFacets(form) {
    const sectionId = form.dataset.sectionId;
    const target = () => document.querySelector(`[data-results="${sectionId}"]`);
    let controller;

    const load = async (params) => {
      const url = `${window.location.pathname}?${params}`;
      controller?.abort();
      controller = new AbortController();
      const results = target();
      results?.classList.add('is-loading');
      results?.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${url}${params ? '&' : ''}section_id=${sectionId}`, { signal: controller.signal });
        const doc = parseHTML(await res.text());
        const fresh = doc.querySelector(`[data-results="${sectionId}"]`);
        const freshFacets = doc.querySelector(`form[data-facets][data-section-id="${sectionId}"]`);
        if (fresh && results) results.replaceWith(fresh);
        if (freshFacets) {
          // Mantém o painel aberto e o foco: só troca contadores e chips ativos.
          $$('[data-facet-sync]', freshFacets).forEach((node) => {
            const current = form.querySelector(`[data-facet-sync="${node.dataset.facetSync}"]`);
            if (current) current.innerHTML = node.innerHTML;
          });
        }
        window.history.replaceState({}, '', url);
        const live = $('[data-results-live]');
        const count = doc.querySelector('[data-results-count]');
        if (live && count) live.textContent = count.textContent.trim();
        reveal(document);
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = url;
      }
    };

    const serialize = () => {
      const fd = new FormData(form);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      return params.toString();
    };

    form.addEventListener('change', debounce(() => load(serialize()), 300));
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      load(serialize());
    });
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-facet-link]');
      if (!a) return;
      e.preventDefault();
      const u = new URL(a.href, window.location.origin);
      // Sincroniza os campos do formulário com o link de remoção
      $$('input[type="checkbox"]', form).forEach((cb) => (cb.checked = u.searchParams.getAll(cb.name).includes(cb.value)));
      $$('input[type="number"], input[type="text"]', form).forEach((inp) => (inp.value = u.searchParams.get(inp.name) || ''));
      load(u.searchParams.toString());
    });
  }

  /* ---------------------------------------------------------------
   * Busca preditiva (/search/suggest + section_id)
   * ------------------------------------------------------------- */
  function initPredictive(wrapper) {
    const input = $('input[type="search"]', wrapper);
    const out = $('[data-predictive-results]', wrapper);
    if (!input || !out) return;
    let controller;
    const run = debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) {
        out.innerHTML = '';
        out.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      controller?.abort();
      controller = new AbortController();
      wrapper.classList.add('is-loading');
      try {
        const url = `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,query&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,tag&section_id=predictive-search`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const content = doc.querySelector('[data-predictive-content]');
        out.innerHTML = content ? content.innerHTML : '';
        out.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      } catch (err) {
        if (err.name !== 'AbortError') {
          out.innerHTML = `<p class="predictive__error" role="alert">${strings.searchError || 'Não foi possível carregar sugestões.'}</p>`;
          out.hidden = false;
        }
      } finally {
        wrapper.classList.remove('is-loading');
      }
    }, 220);
    input.addEventListener('input', run);
    input.addEventListener('keydown', (e) => {
      if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
      const links = $$('a', out);
      if (!links.length) return;
      e.preventDefault();
      links[e.key === 'ArrowDown' ? 0 : links.length - 1].focus();
    });
    out.addEventListener('keydown', (e) => {
      const links = $$('a', out);
      const i = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' && i < links.length - 1) {
        e.preventDefault();
        links[i + 1].focus();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        (i > 0 ? links[i - 1] : input).focus();
      }
    });
  }

  /* ---------------------------------------------------------------
   * Revelação ao rolar. O conteúdo é visível por padrão: só fica oculto
   * quando há JS, o movimento é permitido e o elemento está fora da tela.
   * ------------------------------------------------------------- */
  let io;
  function reveal(ctx) {
    const items = $$('[data-reveal]:not(.is-in)', ctx);
    if (reduceMotion() || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    io =
      io ||
      new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              en.target.classList.add('is-in');
              io.unobserve(en.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    items.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        el.style.setProperty('--reveal-i', i % 6);
        el.classList.add('is-in');
      } else {
        io.observe(el);
      }
    });
    root.classList.add('reveal-ready');
  }

  /* Cabeçalho: estado ao rolar (sólido / transparente, esconder ao descer) */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      if (header.dataset.hideOnScroll === 'true' && !document.body.classList.contains('has-panel')) {
        header.classList.toggle('is-hidden', y > lastY && y > 240);
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(onScroll);
          ticking = true;
        }
      },
      { passive: true }
    );
    onScroll();
  }

  /* Ordenação fora do formulário de filtros (select único) */
  document.addEventListener('change', (e) => {
    const sort = e.target.closest('select[data-sort]');
    if (!sort) return;
    const form = sort.form;
    if (form && form.matches('[data-facets]')) return; // tratado pelo initFacets
    const u = new URL(window.location.href);
    u.searchParams.set('sort_by', sort.value);
    window.location.href = u.toString();
  });


  /* ---------------------------------------------------------------
   * Barra de compra fixa: aparece quando o botão principal sai da tela
   * ------------------------------------------------------------- */
  function initStickyBuy(bar) {
    const anchor = document.querySelector(bar.dataset.stickyBuy);
    if (!anchor || !('IntersectionObserver' in window)) return;
    const io2 = new IntersectionObserver(([en]) => {
      const show = !en.isIntersecting && en.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      $$('a, button', bar).forEach((b) => (b.tabIndex = show ? 0 : -1));
    });
    io2.observe(anchor);
  }

  /* ---------------------------------------------------------------
   * Abas acessíveis (role=tablist), setas do teclado
   * ------------------------------------------------------------- */
  function initTabs(wrap) {
    const tabs = $$('[role="tab"]', wrap);
    const select = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !on;
          if (on) reveal(panel);
        }
      });
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(t));
      t.addEventListener('keydown', (e) => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        const next = tabs[(i + d + tabs.length) % tabs.length];
        next.focus();
        select(next);
      });
    });
  }

  /* ---------------------------------------------------------------
   * Pré-visualização de imagem que segue o cursor (listas-índice).
   * Só em dispositivos com mouse; o conteúdo continua acessível sem ela.
   * ------------------------------------------------------------- */
  function initPreview(list) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const fig = document.createElement('div');
    fig.className = 'cursor-preview';
    fig.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    const first = list.querySelector('[data-preview-src]');
    if (!first) return;
    img.src = first.dataset.previewSrc;
    fig.appendChild(img);
    list.appendChild(fig);
    let x = 0, y = 0, cx = 0, cy = 0, raf = null;
    const loop = () => {
      cx += (x - cx) * 0.18;
      cy += (y - cy) * 0.18;
      fig.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      raf = Math.abs(x - cx) + Math.abs(y - cy) > 0.5 ? requestAnimationFrame(loop) : null;
    };
    list.addEventListener('pointermove', (e) => {
      const r = list.getBoundingClientRect();
      x = e.clientX - r.left + 24;
      y = e.clientY - r.top - 80;
      if (reduceMotion()) { cx = x; cy = y; fig.style.transform = `translate3d(${x}px, ${y}px, 0)`; }
      else if (!raf) raf = requestAnimationFrame(loop);
    });
    $$('[data-preview-src]', list).forEach((row) => {
      row.addEventListener('pointerenter', () => {
        img.src = row.dataset.previewSrc;
        fig.classList.add('is-on');
      });
      row.addEventListener('pointerleave', () => fig.classList.remove('is-on'));
    });
  }


  /* Recomendações de produto (API oficial /recommendations/products) */
  async function initRecommendations(el) {
    if (el.dataset.loaded) return;
    el.dataset.loaded = 'true';
    try {
      const res = await fetch(el.dataset.url);
      const doc = parseHTML(await res.text());
      const fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.innerHTML.trim()) {
        el.innerHTML = fresh.innerHTML;
        el.hidden = false;
        reveal(el);
      }
    } catch (e) {
      /* sem recomendações: a seção continua oculta */
    }
  }

  /* Inicialização (também no Theme Editor) */
  function init(ctx = document) {
    $$('[data-product]', ctx).forEach(initProduct);
    $$('[data-gallery]', ctx).forEach(initGallery);
    $$('form[data-facets]', ctx).forEach(initFacets);
    $$('[data-predictive]', ctx).forEach(initPredictive);
    $$('[data-sticky-buy]', ctx).forEach(initStickyBuy);
    $$('[data-tabs]', ctx).forEach(initTabs);
    $$('[data-preview-list]', ctx).forEach(initPreview);
    $$('[data-recommendations]', ctx).forEach(initRecommendations);
    reveal(ctx);
  }

  document.addEventListener('DOMContentLoaded', () => {
    init();
    initHeader();
  });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:select', (e) => $$('[data-reveal]', e.target).forEach((el) => el.classList.add('is-in')));

  window.Onyx = { openPanel, closePanel, closeAllPanels, reduceMotion, reveal, init };
})();
